import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Car, 
  LayoutDashboard,
  FileText,
  AlertTriangle,
  Store,
  Gift,
  BarChart3,
  Monitor,
  ListChecks,
  Megaphone,
  CreditCard,
  TrendingUp,
  Settings,
  LogOut,
  Sparkles,
  CheckCircle,
  XCircle,
  Search,
  RefreshCw,
  Loader2,
  Mail,
  Send,
  RotateCcw,
  Clock,
  Eye,
  Receipt,
  Wallet,
  DollarSign,
  Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

const navItems = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/admin/dashboard" },
  { icon: FileText, label: "Registrations", path: "/admin/registrations" },
  { icon: AlertTriangle, label: "OCR Exceptions", path: "/admin/ocr-exceptions" },
  { icon: Store, label: "Retailers", path: "/admin/retailers" },
  { icon: Gift, label: "Rewards", path: "/admin/rewards" },
  { icon: Sparkles, label: "Entitlements", path: "/admin/entitlements-v2" },
  { icon: Receipt, label: "Receipts", path: "/admin/receipts" },
  { icon: BarChart3, label: "Analytics", path: "/admin/analytics" },
  { icon: Monitor, label: "Screens", path: "/admin/screens" },
  { icon: ListChecks, label: "Playlists", path: "/admin/playlists" },
  { icon: Megaphone, label: "Advertisers", path: "/admin/advertisers" },
  { icon: TrendingUp, label: "Campaigns", path: "/admin/campaigns" },
  { icon: CreditCard, label: "Billing", path: "/admin/billing" },
  { icon: TrendingUp, label: "Revenue", path: "/admin/revenue" },
  { icon: Wallet, label: "Commercial", path: "/admin/commercial" },
  { icon: DollarSign, label: "Finance Centre", path: "/admin/commercial/finance" },
  { icon: Package, label: "Products & Tariffs", path: "/admin/commercial/products-tariffs" },
  { icon: Settings, label: "Settings", path: "/admin/settings" },
];

export default function AdminReceipts() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [receipts, setReceipts] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailDialog, setDetailDialog] = useState({ open: false, receipt: null });
  const [resendDialog, setResendDialog] = useState({ open: false, receipt: null });
  const [resendEmail, setResendEmail] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("limit", "100");
      
      const [receiptsRes, statsRes] = await Promise.all([
        axios.get(`${API}/receipts?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/receipts/stats`, { withCredentials: true })
      ]);
      
      setReceipts(receiptsRes.data?.receipts || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired, please login again");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    document.cookie = "admin_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/admin");
  };

  const handleResend = async () => {
    if (!resendDialog.receipt) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/receipts/${resendDialog.receipt.receipt_id}/resend`,
        { email: resendEmail || null },
        { withCredentials: true }
      );
      toast.success("Receipt resent successfully");
      setResendDialog({ open: false, receipt: null });
      setResendEmail("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend receipt");
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      sent: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle, label: "Sent" },
      pending: { bg: "bg-yellow-100", text: "text-yellow-700", icon: Clock, label: "Pending" },
      failed: { bg: "bg-red-100", text: "text-red-700", icon: XCircle, label: "Failed" }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return "—";
    const date = new Date(isoString);
    return date.toLocaleString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const filteredReceipts = receipts.filter(receipt => {
    const matchesSearch = !searchTerm || 
      receipt.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.receipt_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.receipt_data?.vehicle_registration?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      receipt.receipt_data?.payment_reference?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="admin-receipts">
      {/* Sidebar */}
      <div className="w-64 bg-slate-900 border-r border-slate-800 flex flex-col">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #2563EB 0%, #10B981 100%)' }}
            >
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">ParkWise</h1>
              <p className="text-xs text-slate-500">Admin Console</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.path === "/admin/receipts";
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-500/20 text-emerald-300"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 p-8 overflow-y-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <Receipt className="w-8 h-8 text-emerald-400" />
                Parking Receipts
              </h1>
              <p className="text-slate-400 mt-1">
                Email receipts for parking payments
              </p>
            </div>
            <Button 
              onClick={fetchData} 
              variant="outline" 
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.sent || 0}</p>
                      <p className="text-xs text-slate-400">Sent Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-yellow-500/20 flex items-center justify-center">
                      <Clock className="w-5 h-5 text-yellow-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.pending || 0}</p>
                      <p className="text-xs text-slate-400">Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                      <XCircle className="w-5 h-5 text-red-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.today?.failed || 0}</p>
                      <p className="text-xs text-slate-400">Failed Today</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                      <Mail className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{stats.all_time?.sent || 0}</p>
                      <p className="text-xs text-slate-400">Total Sent</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="bg-slate-900 border-slate-800 mb-6">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      placeholder="Search by email, vehicle, or reference..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      data-testid="search-input"
                      className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                    />
                  </div>
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px] bg-slate-800 border-slate-700 text-white" data-testid="status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="all" className="text-white">All Status</SelectItem>
                    <SelectItem value="sent" className="text-white">Sent</SelectItem>
                    <SelectItem value="pending" className="text-white">Pending</SelectItem>
                    <SelectItem value="failed" className="text-white">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Receipts Table */}
          <Card className="bg-slate-900 border-slate-800">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-white flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Receipts ({filteredReceipts.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
                </div>
              ) : filteredReceipts.length === 0 ? (
                <div className="text-center py-12">
                  <Receipt className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No receipts found</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Receipts are created when customers request them during payment
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Receipt ID
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Email
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Vehicle
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Payment Ref
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800">
                      {filteredReceipts.map((receipt) => (
                        <tr 
                          key={receipt.receipt_id} 
                          className="hover:bg-slate-800/30 transition-colors"
                          data-testid={`receipt-row-${receipt.receipt_id}`}
                        >
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-white">
                              {receipt.receipt_id?.slice(0, 16)}...
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-white">{receipt.email}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono font-semibold text-white">
                              {receipt.receipt_data?.vehicle_registration || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-slate-300">
                              {receipt.receipt_data?.payment_reference || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-emerald-400 font-semibold">
                              £{(receipt.receipt_data?.amount_paid || 0).toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-300">
                            {formatDateTime(receipt.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            {getStatusBadge(receipt.status)}
                            {receipt.is_mocked && (
                              <span className="ml-2 text-xs text-orange-400">(MOCKED)</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-white"
                                onClick={() => setDetailDialog({ open: true, receipt })}
                                data-testid={`view-btn-${receipt.receipt_id}`}
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-400 hover:text-emerald-300"
                                onClick={() => {
                                  setResendEmail(receipt.email);
                                  setResendDialog({ open: true, receipt });
                                }}
                                data-testid={`resend-btn-${receipt.receipt_id}`}
                              >
                                <RotateCcw className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Resend Dialog */}
      <Dialog open={resendDialog.open} onOpenChange={(open) => !open && setResendDialog({ open: false, receipt: null })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Send className="w-5 h-5 text-emerald-400" />
              Resend Receipt
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-400 mb-4">
              Resend receipt for payment{" "}
              <span className="font-mono font-bold text-white">
                {resendDialog.receipt?.receipt_data?.payment_reference}
              </span>
            </p>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Email Address</label>
              <Input
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="Enter email..."
                type="email"
                className="bg-slate-800 border-slate-700 text-white"
                data-testid="resend-email-input"
              />
              <p className="text-xs text-slate-500 mt-1">Leave empty to use original email</p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setResendDialog({ open: false, receipt: null })}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleResend} 
              disabled={actionLoading}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="confirm-resend-btn"
            >
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Resend Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => !open && setDetailDialog({ open: false, receipt: null })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              Receipt Details
            </DialogTitle>
          </DialogHeader>
          {detailDialog.receipt && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase">Receipt ID</p>
                  <p className="text-white font-mono text-sm">{detailDialog.receipt.receipt_id}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Status</p>
                  {getStatusBadge(detailDialog.receipt.status)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Email</p>
                  <p className="text-white">{detailDialog.receipt.email}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase">Vehicle</p>
                  <p className="text-white font-mono font-bold">
                    {detailDialog.receipt.receipt_data?.vehicle_registration}
                  </p>
                </div>
              </div>
              
              <div className="border-t border-slate-800 pt-4">
                <p className="text-xs text-slate-500 uppercase mb-3">Payment Details</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Payment Reference</p>
                    <p className="text-white font-mono">{detailDialog.receipt.receipt_data?.payment_reference}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Amount Paid</p>
                    <p className="text-emerald-400 font-bold text-lg">
                      £{(detailDialog.receipt.receipt_data?.amount_paid || 0).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Site</p>
                    <p className="text-white">{detailDialog.receipt.receipt_data?.site_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Duration</p>
                    <p className="text-white">{detailDialog.receipt.receipt_data?.duration_display}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Entry Time</p>
                    <p className="text-white">{detailDialog.receipt.receipt_data?.entry_time}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Exit Time</p>
                    <p className="text-white">{detailDialog.receipt.receipt_data?.exit_time}</p>
                  </div>
                  {detailDialog.receipt.receipt_data?.entitlement_discount > 0 && (
                    <div>
                      <p className="text-xs text-slate-500">Entitlement Discount</p>
                      <p className="text-purple-400">
                        -£{detailDialog.receipt.receipt_data.entitlement_discount.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="border-t border-slate-800 pt-4">
                <p className="text-xs text-slate-500 uppercase mb-3">Delivery Info</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-slate-500">Created</p>
                    <p className="text-white">{formatDateTime(detailDialog.receipt.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Sent At</p>
                    <p className="text-white">{formatDateTime(detailDialog.receipt.sent_at) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Send Attempts</p>
                    <p className="text-white">{detailDialog.receipt.send_attempts || 0}</p>
                  </div>
                  {detailDialog.receipt.failure_reason && (
                    <div className="col-span-2">
                      <p className="text-xs text-slate-500">Failure Reason</p>
                      <p className="text-red-400">{detailDialog.receipt.failure_reason}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
